import type { ReactNode } from 'react';
import type { ImageEditorTexts } from './-components/image-editor';
import { useFieldContext, useFieldValue, useIsFieldOptional } from './shared';
import { useFormReadOnly } from './form-read-only';
import { FieldLabelRow } from './field-label-row';
import { ImageUpload } from './-components/image-upload';

/**
 * Form field for image uploads. The field value encodes three states:
 * - `undefined` = no change (keep existing server image)
 * - `File` = new file uploaded
 * - `''` = existing image removed
 */
export default function ImageUploadField(props: {
	currentUrl: string | undefined;
	buttonLabel: string;
	label?: string;
	description?: ReactNode;
	contain?: boolean;
	enableOpenInTab?: boolean;
	enableCrop?: boolean;
	aspectRatio?: { width: number; height: number };
	size?: 'default' | 'compact';
	emptyText?: string;
	noImageText?: string;
	openInNewTabText?: string;
	cropImageText?: string;
	removeImageText?: string;
	dropZoneDescription?: string;
	imageEditorTexts?: Partial<ImageEditorTexts>;
	children?: ReactNode;
}) {
	const field = useFieldContext<File | '' | undefined>();
	const formReadOnly = useFormReadOnly();
	const isOptional = useIsFieldOptional(field.name);
	const value = useFieldValue(field);
	const isRemoved = value === '';

	return (
		<div>
			{props.label && (
				<div className="mb-2">
					<FieldLabelRow
						label={props.label}
						htmlFor={field.name}
						hasError={field.state.meta.isTouched && !field.state.meta.isValid}
						optional={isOptional}
						info={props.description}
					/>
				</div>
			)}
			<ImageUpload
				id={field.name}
				currentUrl={isRemoved ? undefined : props.currentUrl}
				file={value instanceof File ? value : undefined}
				onChange={(file) => field.handleChange(file)}
				onRemove={() => field.handleChange('')}
				label={props.buttonLabel}
				readOnly={formReadOnly}
				contain={props.contain}
				enableOpenInTab={props.enableOpenInTab}
				enableCrop={props.enableCrop}
				aspectRatio={props.aspectRatio}
				size={props.size}
				noImageText={props.noImageText}
				openInNewTabText={props.openInNewTabText}
				cropImageText={props.cropImageText}
				removeImageText={props.removeImageText}
				dropZoneDescription={props.dropZoneDescription}
				imageEditorTexts={props.imageEditorTexts}
			>
				{props.children}
			</ImageUpload>
		</div>
	);
}
